import { useCallback } from 'react'
import useSWR, { Fetcher } from 'swr'
import { client } from '~/api/client'
import {
  createBlockedMember,
  createRelationship,
  deleteBlockedMember,
  deleteRelationship,
  getMemberMemberCurrentMemberStatesV1,
  Schemas,
} from '~/api/gen'
import { assertSuccess } from '~/lib/assertSuccess'
import { useAccessTokenValue } from './useAccessTokenValue'

type Key = [
  operationId: 'getMemberMemberCurrentMemberStatesV1',
  memberIds: number[],
  accessToken: string,
]

const getMemberCurrentMemberState: Fetcher<
  Schemas.get_member_member_current_member_states_response | undefined,
  Key
> = async ([_, memberIds, accessToken]) => {
  if (memberIds.length === 0) return undefined
  try {
    const res = await getMemberMemberCurrentMemberStatesV1(
      client,
      { parameter: { 'member_ids[]': memberIds } },
      { token: accessToken }
    )
    assertSuccess(res)
    return res.data
  } catch {
    return undefined
  }
}

type MemberIdent = { user_name: string; id: number } | null

// eslint-disable-next-line complexity
export const useRelationship = (member: MemberIdent) => {
  const accessToken = useAccessTokenValue()
  const key: Key | null =
    accessToken && member
      ? ['getMemberMemberCurrentMemberStatesV1', [member.id], accessToken]
      : null
  const { data, mutate } = useSWR(key, getMemberCurrentMemberState)

  const follow = useCallback(async () => {
    mutate(
      async (prev) => {
        const userName = member?.user_name
        if (
          userName === undefined ||
          accessToken === null ||
          prev?.member_current_member_states?.[0] === undefined
        )
          return prev
        try {
          const res = await createRelationship(
            client,
            { parameter: {}, requestBody: { user_name: userName } },
            { token: accessToken }
          )
          assertSuccess(res)
          return {
            ...prev,
            member_current_member_states: [
              {
                ...prev.member_current_member_states[0],
                is_following: res.data.to_member.user_name === userName,
              },
            ],
          }
        } catch {
          return prev
        }
      },
      {
        optimisticData(currentData, displayedData) {
          const prev = displayedData ?? currentData
          if (prev?.member_current_member_states[0] === undefined) return
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_following: true },
            ],
          }
        },
      }
    )
  }, [accessToken, member?.user_name, mutate])
  const unfollow = useCallback(async () => {
    mutate(
      async (prev) => {
        const userName = member?.user_name
        if (
          userName === undefined ||
          accessToken === null ||
          prev?.member_current_member_states?.[0] === undefined
        )
          return prev
        try {
          const res = await deleteRelationship(
            client,
            { parameter: { user_name: userName } },
            { token: accessToken }
          )
          assertSuccess(res)
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_following: false },
            ],
          }
        } catch {
          window.alert('※FOLLOWが許可されていません')
          return prev
        }
      },
      {
        optimisticData(currentData, displayedData) {
          const prev = displayedData ?? currentData
          if (prev?.member_current_member_states[0] === undefined) return
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_following: false },
            ],
          }
        },
      }
    )
  }, [accessToken, member?.user_name, mutate])
  const block = useCallback(async () => {
    if (!window.confirm('このユーザーをブロックします。よろしいですか？'))
      return
    mutate(
      async (prev) => {
        const userName = member?.user_name
        if (
          userName === undefined ||
          accessToken === null ||
          prev?.member_current_member_states?.[0] === undefined
        )
          return prev
        try {
          const res = await createBlockedMember(
            client,
            { parameter: {}, requestBody: { user_name: userName } },
            { token: accessToken }
          )
          assertSuccess(res)
          return {
            ...prev,
            member_current_member_states: [
              {
                ...prev.member_current_member_states[0],
                is_blocking: res.data.to_member.user_name === userName,
              },
            ],
          }
        } catch {
          return prev
        }
      },
      {
        optimisticData(currentData, displayedData) {
          const prev = displayedData ?? currentData
          if (prev?.member_current_member_states[0] === undefined) return
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_blocking: true },
            ],
          }
        },
      }
    )
  }, [accessToken, member?.user_name, mutate])
  const unblock = useCallback(async () => {
    mutate(
      async (prev) => {
        const userName = member?.user_name
        if (
          userName === undefined ||
          accessToken === null ||
          prev?.member_current_member_states?.[0] === undefined
        )
          return prev
        try {
          const res = await deleteBlockedMember(
            client,
            { parameter: { user_name: userName } },
            { token: accessToken }
          )
          assertSuccess(res)
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_blocking: false },
            ],
          }
        } catch {
          return prev
        }
      },
      {
        optimisticData(currentData, displayedData) {
          const prev = displayedData ?? currentData
          if (prev?.member_current_member_states[0] === undefined) return
          return {
            ...prev,
            member_current_member_states: [
              { ...prev.member_current_member_states[0], is_blocking: false },
            ],
          }
        },
      }
    )
  }, [accessToken, member?.user_name, mutate])
  return {
    following: data?.member_current_member_states?.[0]?.is_following ?? false,
    followed: data?.member_current_member_states?.[0]?.is_followed ?? false,
    blocking: data?.member_current_member_states?.[0]?.is_blocking ?? false,
    blocked: data?.member_current_member_states?.[0]?.is_blocked ?? false,
    follow,
    unfollow,
    block,
    unblock,
  }
}
